import {
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Typography
} from '@material-ui/core'
import { CameraIcon } from 'lucide-react'
import { useRef, useState } from 'react'
import AvatarEditor from 'react-avatar-editor'
import { toast } from 'react-toastify'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import { useStyles } from './style'

const stringToColor = string => {
  if (!string) return 'hsl(0, 0%, 50%)' // Fallback cinza

  let hash = 0
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash)
  }

  const h = Math.abs(hash) % 360 // Matiz aleatória de 0 a 360
  return `hsl(${h}, 70%, 45%)` // 70% saturação, 45% luminosidade
}

const getInitials = name => {
  if (!name) return ''
  const names = name.trim().split(' ')
  if (names.length === 1) return names[0].charAt(0).toUpperCase()
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase()
}

export const AvatarEditorModal = ({
  currentAvatar,
  userName,
  onSaveFile,
  disabled,
  isReadOnly
}) => {
  const classes = useStyles()
  const fileInputRef = useRef(null)
  const editorRef = useRef(null)

  const [imageFile, setImageFile] = useState(null)
  const [scale, setScale] = useState(1.2)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const isEditable = !isReadOnly && !disabled

  const handleFileChange = e => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      if (file.size > 10 * 1024 * 1024) {
        toast.error('A imagem excede o limite de 10MB.')
        e.target.value = ''
        return
      }
      setImageFile(file)
      setIsModalOpen(true)
      setScale(1.2)
    }
    e.target.value = ''
  }

  const handleSave = () => {
    if (!editorRef.current) return
    setIsProcessing(true)
    const canvas = editorRef.current.getImageScaledToCanvas()

    canvas.toBlob(
      blob => {
        if (!blob) {
          toast.error('Erro ao processar imagem.')
          setIsProcessing(false)
          return
        }
        const croppedFile = new File([blob], 'avatar.jpg', {
          type: 'image/jpeg'
        })
        onSaveFile(croppedFile)
        handleClose()
        setIsProcessing(false)
      },
      'image/jpeg',
      0.8
    )
  }

  const handleClose = () => {
    setIsModalOpen(false)
    setImageFile(null)
  }

  const hasAvatar = Boolean(currentAvatar)
  const dynamicAvatarStyle =
    !hasAvatar && userName
      ? {
          backgroundColor: stringToColor(userName),
          color: '#ffffff',
          fontWeight: 'bold'
        }
      : {}

  return (
    <div
      className={classes.root}
      onMouseEnter={() => isEditable && setIsHovered(true)}
      onMouseLeave={() => isEditable && setIsHovered(false)}
    >
      <input
        type="file"
        accept="image/jpeg, image/png, image/webp"
        ref={fileInputRef}
        onChange={handleFileChange}
        className={classes.hiddenInput}
        disabled={!isEditable}
        aria-label="Selecionar imagem de perfil"
      />

      <Avatar
        src={currentAvatar || undefined} // Se não houver, passamos undefined para o MUI renderizar os children
        alt={userName || 'Avatar'}
        className={`${classes.avatar} ${isEditable ? classes.avatarEditable : ''}`}
        style={dynamicAvatarStyle} // Aplica a cor de fundo gerada
        onClick={() => {
          if (isEditable && fileInputRef.current) {
            fileInputRef.current.click()
          }
        }}
        role={isEditable ? 'button' : 'img'}
        aria-disabled={!isEditable}
      >
        {/* Renderiza as iniciais dentro do Avatar caso não tenha foto */}
        {!hasAvatar && userName ? getInitials(userName) : null}
      </Avatar>

      {isEditable && isHovered && (
        <CameraIcon size={24} className={classes.overlayIcon} />
      )}

      <Dialog
        open={isModalOpen}
        fullWidth
        maxWidth="xs"
        onClose={(event, reason) => {
          // Se estiver processando a imagem, bloqueia QUALQUER fechamento
          if (isProcessing) return

          // Bloqueia o fechamento se o usuário clicar fora do modal
          if (reason === 'backdropClick') return

          handleClose()
        }}
        disableEscapeKeyDown={isProcessing}
      >
        <DialogTitle className={classes.dialogTitle}>
          Ajustar Foto de Perfil
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.editorContainer}>
            {imageFile && (
              <AvatarEditor
                ref={editorRef}
                image={imageFile}
                width={250}
                height={250}
                border={20}
                borderRadius={125}
                color={[0, 0, 0, 0.6]}
                scale={scale}
                rotate={0}
                crossOrigin="anonymous"
              />
            )}
            <div className={classes.sliderContainer}>
              <Typography variant="overline" color="textSecondary">
                Ajustar Zoom
              </Typography>
              <Slider
                value={scale}
                min={1}
                max={3}
                step={0.05}
                onChange={(e, newValue) => setScale(newValue)}
                aria-label="Ajustar zoom da imagem"
                disabled={isProcessing}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <ButtonSecondary
            onClick={handleClose}
            disabled={isProcessing}
            label="Cancelar"
          />
          <ButtonPrimary
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={isProcessing}
            label="Salvar"
          />
        </DialogActions>
      </Dialog>
    </div>
  )
}
